// register.jsx - PyK7VM Account Registration
// Compiled to dist/register.js by build.mjs. Loaded by register.php.

const { useState, useEffect } = React;
const { Aurora } = window;

// ─── USE-CASE OPTIONS ─────────────────────────────────────────────────────────
const USE_CASES = [
  { id: 'indie_dev', label: 'Indie Dev / SaaS', desc: 'Commercial apps & tooling' },
  { id: 'security', label: 'Security Research', desc: 'Reverse engineering analysis' },
  { id: 'enterprise', label: 'Enterprise Team', desc: 'Internal IP & infrastructure' },
  { id: 'student', label: 'Student / Academic', desc: 'Educational & research code' },
  { id: 'other', label: 'Other Projects', desc: 'Custom Python utilities' },
];

// ─── CLEAN SVG ARROW ICON ───────────────────────────────────────────────────
function SvgArrowRight({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ─── THEME TOGGLE BUTTON ───────────────────────────────────────────────────────
function ThemeToggleBtn({ style = {}, showLabel = true, className = '' }) {
  const [theme, setTheme] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const handleThemeChange = (e) => {
      setTheme(e.detail?.theme || (document.documentElement.classList.contains('light') ? 'light' : 'dark'));
    };
    window.addEventListener('pk7vm-theme-change', handleThemeChange);
    return () => window.removeEventListener('pk7vm-theme-change', handleThemeChange);
  }, []);

  const toggle = () => {
    if (window.__PK7VM_THEME__?.toggleTheme) {
      const next = window.__PK7VM_THEME__.toggleTheme();
      setTheme(next);
    } else {
      const isLight = document.documentElement.classList.contains('light');
      const next = isLight ? 'dark' : 'light';
      if (next === 'light') {
        document.documentElement.classList.add('light');
        document.documentElement.setAttribute('data-theme', 'light');
      } else {
        document.documentElement.classList.remove('light');
        document.documentElement.setAttribute('data-theme', 'dark');
      }
      try { localStorage.setItem('pk7vm_theme', next); } catch (e) {}
      setTheme(next);
    }
  };

  const isLight = theme === 'light';

  return (
    <button
      type="button"
      onClick={toggle}
      className={`pk7vm-nav-theme-btn ${className}`}
      aria-label={isLight ? 'Switch to Dark mode' : 'Switch to Light mode'}
      title={isLight ? 'Switch to Dark mode (Ctrl+Shift+L)' : 'Switch to Light mode (Ctrl+Shift+L)'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        padding: showLabel ? '7px 14px 7px 11px' : '7px 10px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        transition: 'all .2s ease',
        minHeight: 36,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <span className="pk7vm-theme-icon-wrap" aria-hidden="true" style={{ width: 16, height: 16, position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-sun" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 0 : 1, transform: isLight ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)', transition: 'transform .35s, opacity .3s' }}>
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-moon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 1 : 0, transform: isLight ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)', transition: 'transform .35s, opacity .3s' }}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      </span>
      {showLabel && <span className="pk7vm-theme-toggle-label">{isLight ? 'Dark' : 'Light'}</span>}
    </button>
  );
}

// ─── AUTH NAVIGATION HEADER ───────────────────────────────────────────────────
function AuthNav({ rightLabel, rightHref }) {
  const [hovered, setHovered] = useState(false);

  return (
    <nav
      className="auth-nav"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 clamp(16px, 4vw, 32px)',
        height: 64,
        background: 'rgba(8,8,13,0.7)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <a
        href="index.php"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          fontWeight: 600,
          letterSpacing: -0.4,
          fontSize: 16,
          textDecoration: 'none',
          color: 'var(--text)',
          minHeight: 44,
        }}
      >
        <img
          src="favicon.ico"
          alt="PyK7VM Logo"
          aria-hidden="true"
          style={{ width: 24, height: 24, borderRadius: 6 }}
        />
        <span>PyK7VM</span>
      </a>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <ThemeToggleBtn />
        <a
          href={rightHref}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13.5,
            fontWeight: 500,
            color: hovered ? 'var(--text)' : 'var(--text-muted)',
            textDecoration: 'none',
            transition: 'color .2s ease',
            minHeight: 44,
          }}
        >
          <span>{rightLabel}</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              transform: hovered ? 'translateX(3px)' : 'none',
              transition: 'transform .2s ease',
            }}
          >
            <SvgArrowRight size={13} color="currentColor" />
          </span>
        </a>
      </div>
    </nav>
  );
}

// ─── FORM FIELD WRAPPER ───────────────────────────────────────────────────────
function FormField({ id, label, extra, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={id}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--text-dim)',
          marginBottom: 7,
          letterSpacing: 0.1,
        }}
      >
        <span>{label}</span>
        {extra && <span style={{ color: 'var(--text-muted)', fontSize: 11.5, fontWeight: 400 }}>{extra}</span>}
      </label>
      {children}
    </div>
  );
}

// ─── PASSWORD STRENGTH HELPER ─────────────────────────────────────────────────
function calculateStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[0-9]/.test(pw) && /[a-zA-Z]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;

  if (score <= 1) return { score: 1, label: 'Weak', color: '#ef4444' };
  if (score === 2) return { score: 2, label: 'Fair', color: '#f59e0b' };
  if (score === 3) return { score: 3, label: 'Good', color: '#3b82f6' };
  return { score: 4, label: 'Strong', color: '#10b981' };
}

// ─── REGISTER APPLICATION ROOT ────────────────────────────────────────────────
function RegisterApp() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [useCase, setUseCase] = useState('indie_dev');
  const [detail, setDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [btnHover, setBtnHover] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [detailFocused, setDetailFocused] = useState(false);

  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA') {
        submit();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  const strength = calculateStrength(password);

  async function submit() {
    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!useCase) {
      setError('Please select a primary use case.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('api/auth/register.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          occupation_cat: useCase,
          occupation_detail: detail.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Registration failed. Try another email.');
        setLoading(false);
        return;
      }
      window.location.href = 'forge.php';
    } catch (e) {
      setError('Network connection error. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.35 }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#08080d' }} />
      </div>

      <AuthNav rightLabel="Sign in" rightHref="login.php" />

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(80px, 12vw, 110px) clamp(16px, 4vw, 24px) clamp(30px, 6vw, 60px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ width: '100%', maxWidth: 480 }}>
          
          {/* Header Title */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1
              style={{
                fontSize: 'clamp(26px, 4vw, 32px)',
                fontWeight: 500,
                letterSpacing: -1.2,
                lineHeight: 1.15,
                color: 'var(--text)',
                marginBottom: 8,
              }}
            >
              Create your account.
            </h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(13.5px, 1.8vw, 14.5px)', margin: 0, lineHeight: 1.5 }}>
              Create a free beta account to build and manage protected artifacts. No credit card required.
            </p>
          </div>

          {/* Centered Glassmorphic Card */}
          <div
            className="auth-card"
            style={{
              background: 'rgba(245,241,232,0.015)',
              border: '1px solid var(--border-strong)',
              borderRadius: 22,
              padding: 'clamp(24px, 4vw, 36px) clamp(18px, 4vw, 32px) 28px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), inset 0 0 20px rgba(59,130,246,0.03)',
            }}
          >
            <style>{`
              html.light .auth-card {
                background: #ffffff !important;
                border-color: rgba(15, 23, 42, 0.08) !important;
                box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.04) !important;
              }
              html.light .auth-input {
                background: #ffffff !important;
                color: #0f172a !important;
                border-color: rgba(15, 23, 42, 0.14) !important;
              }
              html.light .use-case-btn {
                background: #f8fafc !important;
                border-color: rgba(15, 23, 42, 0.08) !important;
              }
              html.light .use-case-btn:hover {
                background: #f1f5f9 !important;
                border-color: rgba(37, 99, 235, 0.3) !important;
              }
              html.light .use-case-btn.is-selected {
                background: #eff6ff !important;
                border-color: rgba(37, 99, 235, 0.6) !important;
                box-shadow: 0 0 14px rgba(37, 99, 235, 0.12) !important;
              }
              html.light .auth-nav {
                background: rgba(255, 255, 255, 0.9) !important;
                border-bottom-color: rgba(15, 23, 42, 0.08) !important;
              }
            `}</style>
            {/* Error Notification */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#f87171',
                  padding: '12px 14px',
                  borderRadius: 12,
                  fontSize: 13.5,
                  fontWeight: 500,
                  marginBottom: 20,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* 1. Full Name */}
            <FormField id="f-name" label="Full name">
              <input
                id="f-name"
                type="text"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                autoComplete="name"
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                className="auth-input"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'rgba(6,6,12,0.85)',
                  border: `1px solid ${nameFocused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
                  boxShadow: nameFocused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                  color: 'var(--text)',
                  borderRadius: 12,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color .2s ease, box-shadow .2s ease',
                  WebkitAppearance: 'none',
                  minHeight: 44,
                }}
              />
            </FormField>

            {/* 2. Email Address */}
            <FormField id="f-email" label="Email address">
              <input
                id="f-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                className="auth-input"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'rgba(6,6,12,0.85)',
                  border: `1px solid ${emailFocused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
                  boxShadow: emailFocused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                  color: 'var(--text)',
                  borderRadius: 12,
                  fontFamily: 'var(--font-code)',
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color .2s ease, box-shadow .2s ease',
                  WebkitAppearance: 'none',
                  minHeight: 44,
                }}
              />
            </FormField>

            {/* 3. Password & Live Strength Meter */}
            <FormField id="f-password" label="Password" extra="Min. 8 characters">
              <div style={{ position: 'relative' }}>
                <input
                  id="f-password"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  onFocus={() => setPwFocused(true)}
                  onBlur={() => setPwFocused(false)}
                  className="auth-input"
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 14px',
                    background: 'rgba(6,6,12,0.85)',
                    border: `1px solid ${pwFocused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
                    boxShadow: pwFocused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                    color: 'var(--text)',
                    borderRadius: 12,
                    fontFamily: 'var(--font-ui)',
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color .2s ease, box-shadow .2s ease',
                    WebkitAppearance: 'none',
                    minHeight: 44,
                  }}
                />
                <button
                  onClick={() => setShowPw((s) => !s)}
                  type="button"
                  tabIndex={-1}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    padding: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'color .2s ease',
                    minWidth: 36,
                    minHeight: 36,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                >
                  {showPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Real-time Strength Meter Bar */}
              {password.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4, height: 4, marginBottom: 4 }}>
                    {[1, 2, 3, 4].map((step) => (
                      <div
                        key={step}
                        style={{
                          flex: 1,
                          borderRadius: 999,
                          background: step <= strength.score ? strength.color : 'rgba(245,241,232,0.1)',
                          transition: 'background .25s ease',
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-muted)' }}>
                    <span>Password strength</span>
                    <span style={{ color: strength.color, fontWeight: 600 }}>{strength.label}</span>
                  </div>
                </div>
              )}
            </FormField>

            {/* 4. Interactive Use-Case Grid Pills */}
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: 'var(--text-dim)',
                  marginBottom: 8,
                  letterSpacing: 0.1,
                }}
              >
                Primary use case
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 8,
                }}
              >
                {USE_CASES.map((item) => {
                  const isSelected = useCase === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setUseCase(item.id)}
                      disabled={loading}
                      className={`use-case-btn ${isSelected ? 'is-selected' : ''}`}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: isSelected ? 'rgba(59,130,246,0.12)' : 'var(--bg)',
                        border: isSelected ? '1px solid rgba(59,130,246,0.6)' : '1px solid var(--border)',
                        color: isSelected ? 'var(--text)' : 'var(--text-dim)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all .2s ease',
                        boxShadow: isSelected ? '0 0 14px rgba(59,130,246,0.15)' : 'none',
                        minHeight: 44,
                      }}
                    >
                      <div style={{ fontSize: 12.5, fontWeight: isSelected ? 600 : 500, letterSpacing: -0.2, color: 'var(--text)' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {item.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 5. Optional Project Details */}
            <FormField id="f-detail" label="Project context" extra="(optional)">
              <textarea
                id="f-detail"
                placeholder="Briefly describe the Python algorithms or scripts you intend to protect."
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                disabled={loading}
                rows={2}
                onFocus={() => setDetailFocused(true)}
                onBlur={() => setDetailFocused(false)}
                className="auth-input"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'rgba(6,6,12,0.85)',
                  border: `1px solid ${detailFocused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
                  boxShadow: detailFocused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                  color: 'var(--text)',
                  borderRadius: 12,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 13.5,
                  outline: 'none',
                  resize: 'vertical',
                  lineHeight: 1.6,
                  minHeight: 64,
                  transition: 'border-color .2s ease, box-shadow .2s ease',
                }}
              />
            </FormField>

            {/* Submit CTA Button */}
            <button
              onClick={submit}
              disabled={loading}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => setBtnHover(false)}
              style={{
                width: '100%',
                padding: '13px',
                marginTop: 4,
                background: 'var(--text)',
                opacity: loading ? 0.7 : btnHover ? 0.92 : 1,
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 14.5,
                cursor: loading ? 'default' : 'pointer',
                letterSpacing: -0.2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                transform: btnHover && !loading ? 'translateY(-1px)' : 'none',
                transition: 'all .2s ease',
                minHeight: 46,
              }}
            >
              {loading ? 'Provisioning account…' : 'Create Free Account'}
            </button>

            {/* Sign In Link */}
            <p style={{ textAlign: 'center', marginTop: 20, marginBottom: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>
              Already have an account?{' '}
              <a
                href="login.php"
                style={{
                  color: 'var(--brand-soft)',
                  fontWeight: 500,
                  textDecoration: 'none',
                  borderBottom: '1px solid rgba(59,130,246,0.3)',
                }}
              >
                Sign in
              </a>
            </p>
          </div>

          {/* Privacy Policy Footer Note */}
          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            By registering, you agree to our{' '}
            <a
              href="privacy.php"
              style={{
                color: 'var(--brand-soft)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(59,130,246,0.25)',
              }}
            >
              Data Privacy &amp; Ephemerality Policy
            </a>.
          </p>

        </div>
      </main>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(RegisterApp));
